import { useState } from 'react';
import { useLogin, useNotification } from '@refinedev/core';
import { Form, Input, Button, Card, Typography, Space, Checkbox, Flex } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ErrorAlert } from '../../components';
import type { LoginCredentials } from '../../types/auth';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../../utils/constants';
import './login.css';
import { usePageTitle } from '../../hooks';

const { Title, Text, Link } = Typography;

export const Login = () => {
  const [form] = Form.useForm();
  const { mutate: login } = useLogin<LoginCredentials>();
  const { open } = useNotification();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Set page title
  usePageTitle(t('auth.login', 'Iniciar Sesión'));

  const onFinish = async (values: LoginCredentials) => {
    setErrorMessage('');
    setIsLoading(true);

    login(values, {
      onSuccess: () => {
        setIsLoading(false);
        // Show success notification
        open?.({
          type: 'success',
          message: t('auth.login_success'),
          description: SUCCESS_MESSAGES.LOGIN_SUCCESS,
        });
      },
      onError: (error) => {
        setIsLoading(false);
        
        // Determine error message
        let errorMsg: string = ERROR_MESSAGES.INVALID_CREDENTIALS;
        
        if (error?.message) {
          errorMsg = error.message;
        } else if (error?.name === 'NetworkError') {
          errorMsg = ERROR_MESSAGES.NETWORK_ERROR;
        } else if (error?.name === 'LoginError') {
          errorMsg = ERROR_MESSAGES.INVALID_CREDENTIALS;
        }
        
        // Set error message for inline display
        setErrorMessage(errorMsg);
        
        // Show error notification
        open?.({
          type: 'error',
          message: t('auth.login_error'),
          description: errorMsg,
        });
      },
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundImage: 'url(/background_cvp.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '20px',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 0,
        }}
      />
      <Card
        className="login-card"
        style={{
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
          borderRadius: 12,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <img 
              src="/logo_cyberagropolis.png" 
              alt="Cyberagropolis" 
              style={{ 
                maxWidth: '350px',
                width: '100%',
                height: 'auto',
                marginBottom: 16,
              }}
            />
            <Text style={{ color: '#6b7280', fontSize: '15px' }}>
              {t('auth.sign_in_account', 'Sign in to your account')}
            </Text>
          </div>

          {errorMessage && (
            <ErrorAlert
              message={errorMessage}
              onClose={() => setErrorMessage('')}
            />
          )}

          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
            size="large"
            initialValues={{ remember: true }}
          >
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: `${t('auth.username')} ${t('common.required')}`,
                },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                placeholder={t('auth.username')}
                autoComplete="username"
                disabled={isLoading}
                style={{
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f9fafb',
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: `${t('auth.password')} ${t('common.required')}`,
                },
              ]}
              style={{ marginBottom: 12 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                placeholder={t('auth.password')}
                autoComplete="current-password"
                disabled={isLoading}
                style={{
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f9fafb',
                }}
              />
            </Form.Item>

            <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox 
                  disabled={isLoading}
                  style={{ 
                    color: '#6b7280',
                    fontSize: '14px',
                  }}
                >
                  <span style={{ color: '#6b7280' }}>{t('auth.remember_me')}</span>
                </Checkbox>
              </Form.Item>
              <Link 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  // TODO: Implement forgot password functionality
                }}
                style={{ 
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('auth.forgot_password')}
              </Link>
            </Flex>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                className="login-button"
                style={{ 
                  height: 44,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  fontWeight: 500,
                  fontSize: '15px',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.2s ease',
                }}
              >
                {isLoading ? `${t('common.loading')}` : t('auth.login')}
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};
